<div class="h-screen relative overflow-hidden flex flex-col justify-between bg-[#f5f5f7] dark:bg-[#050505] text-slate-900 dark:text-gray-100 transition-colors duration-500 text-[14px] leading-normal antialiased selection:bg-indigo-500/30">

  <!-- Navigation -->
  <nav class="fixed top-0 w-full z-50 bg-[#f5f5f7]/90 dark:bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-gray-200 dark:border-white/5 transition-all duration-300">
    <div class="w-full px-6 h-[64px] flex items-center justify-between">
      <div class="flex items-center gap-4 group">
        <div class="w-8 h-8 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-black flex items-center justify-center shadow-sm">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
          </svg>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-gray-300 dark:text-gray-700 font-light text-xl">/</span>
          <span class="font-sans font-medium text-[15px] tracking-tight text-slate-900 dark:text-white">Home</span>
        </div>
      </div>
      
      <div class="flex items-center gap-6">
        <!-- Theme Toggles -->
        <div class="flex items-center p-1 rounded-full bg-gray-100/80 dark:bg-white/5 border border-gray-200/50 dark:border-white/5 backdrop-blur-md">
          <button (click)="setTheme('light')" [ngClass]="{'text-indigo-600 bg-white shadow-sm': currentTheme === 'light'}" class="p-1.5 rounded-full text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200 hover:bg-white dark:hover:bg-white/10">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
            </svg>
          </button>
          <button (click)="setTheme('dark')" [ngClass]="{'shadow-sm': currentTheme === 'dark'}" class="p-1.5 rounded-full text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200 hover:bg-white dark:hover:bg-white/10">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
            </svg>
          </button>
        </div>

        <div class="flex items-center gap-6">
          <a routerLink="/analysis-report" class="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium text-[13px] transition-colors">
            Photo QC Analysis
          </a>
        </div>
      </div>
    </div>
  </nav>

  <!-- Main Content -->
  <main class="relative z-10 pt-[64px] w-full h-full flex flex-col flex-grow overflow-hidden transition-colors duration-500">
    
    <!-- Question List -->
    <div class="w-full fade-in mx-auto h-full overflow-y-auto hide-scroll pb-24 px-8 pt-16">
      
      <!-- Header Section with Stats Cards -->
      <div class="flex items-start justify-between gap-6 mb-8">
        <!-- Left: Title -->
        <div class="flex flex-col items-start flex-1">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100/80 dark:border-blue-500/20 mb-4 shadow-sm">
            <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            <span class="text-blue-600 dark:text-blue-400 text-[10px] font-bold uppercase tracking-widest">Intelligence Hub</span>
          </div>
          
          <h2 class="text-3xl font-semibold tracking-tight text-[#0F172A] dark:text-white mb-2">
            {{ templateName || 'Questionnaire Creator' }}
          </h2>
          <p class="text-gray-500 dark:text-gray-400 text-base font-normal leading-relaxed">
            <span *ngIf="templateId">Template ID: {{ templateId }} — Select a question below to create prompts.</span>
            <span *ngIf="!templateId">Design data collection workflows. Manage your existing queries or create new intelligence prompts below.</span>
          </p>
        </div>

        <!-- Right: Stats Cards (horizontal row) -->
        <div *ngIf="templateId && tasks.length > 0" class="flex gap-3 flex-shrink-0">
          <!-- Total Questions Card - Purple -->
          <div class="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-4 min-w-[140px] shadow-md shadow-violet-500/20 relative overflow-hidden">
            <div class="flex items-start justify-between mb-2">
              <p class="text-white/90 font-medium text-xs">Total Questions</p>
              <div class="flex items-center gap-1 text-white/70 text-[10px]">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                </svg>
                <span>All time</span>
              </div>
            </div>
            <p class="text-2xl font-bold text-white mb-2">{{ totalQuestions }}</p>
            <div class="flex items-end justify-between">
              <span class="text-white/60 text-[10px]">See all</span>
              <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Has Prompt Card - Green -->
          <div class="bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl p-4 min-w-[140px] shadow-md shadow-emerald-500/20 relative overflow-hidden">
            <div class="flex items-start justify-between mb-2">
              <p class="text-white/90 font-medium text-xs">Has Prompt</p>
              <div class="flex items-center gap-1 text-white/70 text-[10px]">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
                <span>Ready</span>
              </div>
            </div>
            <p class="text-2xl font-bold text-white mb-2">{{ questionsWithPrompt }}</p>
            <div class="flex items-end justify-between">
              <span class="text-white/60 text-[10px]">See all</span>
              <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              </div>
            </div>
          </div>

          <!-- No Prompt Card - Orange -->
          <div class="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-4 min-w-[140px] shadow-md shadow-amber-500/20 relative overflow-hidden">
            <div class="flex items-start justify-between mb-2">
              <p class="text-white/90 font-medium text-xs">No Prompt</p>
              <div class="flex items-center gap-1 text-white/70 text-[10px]">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
                <span>Pending</span>
              </div>
            </div>
            <p class="text-2xl font-bold text-white mb-2">{{ questionsWithoutPrompt }}</p>
            <div class="flex items-end justify-between">
              <span class="text-white/60 text-[10px]">See all</span>
              <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-16">
        <div class="relative">
          <div class="w-12 h-12 rounded-full border-2 border-gray-200 dark:border-gray-700"></div>
          <div class="absolute top-0 left-0 w-12 h-12 rounded-full border-2 border-transparent border-t-blue-500 animate-spin"></div>
        </div>
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400 animate-pulse">Loading questions...</p>
      </div>

      <!-- No Template Loaded State -->
      <div *ngIf="!isLoading && !templateId" class="flex flex-col items-center justify-center py-24">
        <div class="w-20 h-20 rounded-2xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center mb-6">
          <svg class="w-10 h-10 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Template Loaded</h3>
        <p class="text-gray-500 dark:text-gray-400 text-sm mb-6">Please select a template from the Templates page to view questions.</p>
        <a routerLink="/analysis-report" class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          Go to Templates
        </a>
      </div>

      <!-- Questions List - Full Width -->
      <div *ngIf="!isLoading && templateId" class="bg-white dark:bg-[#111111]/60 dark:backdrop-blur-md rounded-2xl overflow-hidden shadow-[0_4px_24px_-8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_24px_-4px_rgba(0,0,0,0.4)] border border-gray-100 dark:border-white/5 transition-colors duration-300">
        
        <!-- Search Bar -->
        <div class="px-8 py-4 border-b border-gray-100 dark:border-white/5">
          <div class="relative max-w-md">
            <svg class="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input 
              type="text" 
              [(ngModel)]="searchQuery"
              placeholder="Search questions by name or ID..."
              class="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all text-sm"
            />
          </div>
        </div>

        <!-- Table Header - Sortable -->
        <div class="grid grid-cols-[1fr_120px_160px] items-center px-8 py-4 border-b border-gray-100 dark:border-white/5 bg-gradient-to-r from-gray-50/80 to-gray-50/40 dark:from-white/5 dark:to-transparent">
          <button (click)="toggleSort('name')" class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-left">
            Question Name
            <svg *ngIf="sortColumn === 'name'" class="w-3 h-3" [class.rotate-180]="sortDirection === 'desc'" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
          </button>
          <button (click)="toggleSort('status')" class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            Status
            <svg *ngIf="sortColumn === 'status'" class="w-3 h-3" [class.rotate-180]="sortDirection === 'desc'" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
          </button>
          <div class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-right pr-2">Action</div>
        </div>

        <div class="divide-y divide-gray-50 dark:divide-white/5">
          
          <!-- Empty State -->
          <div *ngIf="tasks.length === 0 && !isLoading" class="flex flex-col items-center justify-center py-16 px-8">
            <div class="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Questions Found</h3>
          </div>

          <!-- Task Items -->
          <div *ngFor="let task of filteredTasks" (click)="selectTask(task)" class="grid grid-cols-[1fr_120px_160px] items-center px-8 py-5 group hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors duration-200 cursor-pointer">
            <div class="flex items-center gap-5">
              <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 border border-blue-100/50 dark:border-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-105 transition-transform duration-200 shadow-sm">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
                </svg>
              </div>
              <div class="flex flex-col gap-0.5">
                <h3 class="text-[15px] font-medium text-gray-900 dark:text-gray-200 tracking-tight">{{ task.name }}</h3>
                <p class="text-[13px] text-gray-500 dark:text-gray-500 font-normal">{{ task.description }}</p>
              </div>
            </div>
            
            <div *ngIf="task.isEdit" class="flex items-center">
              <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-medium border border-emerald-100 dark:border-emerald-500/20">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Has Prompt
              </div>
            </div>
            <div *ngIf="!task.isEdit" class="flex items-center">
              <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 dark:bg-white/5 text-gray-400 dark:text-gray-500 text-[11px] font-medium border border-gray-100 dark:border-white/10">
                <span class="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600"></span>
                No Prompt
              </div>
            </div>
            
            <div class="text-right">
              <button *ngIf="!task.isEdit" (click)="selectTask(task)" class="inline-flex items-center justify-center w-full gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 dark:from-white dark:to-gray-100 text-white dark:text-slate-900 hover:from-slate-800 hover:to-slate-700 dark:hover:from-gray-100 dark:hover:to-gray-200 transition-all duration-300 shadow-lg shadow-slate-900/10 dark:shadow-white/10 hover:shadow-xl hover:shadow-slate-900/20 dark:hover:shadow-white/20 hover:-translate-y-0.5 group/btn">
                <svg class="w-4 h-4 text-indigo-300 dark:text-indigo-600 group-hover/btn:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                </svg>
                <span class="text-[13px] font-semibold">Create</span>
              </button>
              <button *ngIf="task.isEdit" (click)="selectTask(task)" class="inline-flex items-center justify-center w-full gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 dark:from-violet-400 dark:to-purple-500 text-white hover:from-violet-600 hover:to-purple-700 dark:hover:from-violet-500 dark:hover:to-purple-600 transition-all duration-300 shadow-lg shadow-violet-500/25 hover:shadow-xl hover:shadow-violet-500/40 hover:-translate-y-0.5 group/btn">
                <svg class="w-4 h-4 group-hover/btn:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                </svg>
                <span class="text-[13px] font-semibold">Edit</span>
              </button>
            </div>
          </div>

        </div>
      </div>
      <div class="h-24"></div>
    </div>

  </main>

</div>
